import React, { useState } from "react";
import useMediaQuery from "../../hooks/useMediaQuery";
import { mq } from "../../styles/canonical/breakpoints";
import {
  CardItem,
  CardMeta,
  CardSub,
  CardTitulo,
  CardTopo as CardTopoCompacto,
} from "../../components/ListaCompacta/styles";
import { formatarData } from "../../utils/calculos";
import { getColunasMaquinas } from "../../utils/industrializacao";
import { LISTA_OPERACOES } from "./dominio";
import {
  Ambiente,
  Ambientes,
  Card,
  CardCliente,
  CardLote,
  CardRodape,
  CardTopo,
  Coluna,
  ColunaAtraso,
  ColunaContagem,
  ColunaTitulo,
  ColunaTopo,
  ColunaVazia,
  Colunas,
  Legenda,
  LaneChip,
  LaneTabs,
  Lista,
  Marcador,
  Marcadores,
  Prazo,
} from "./kanbanStyles";
import { EmptyState, OpMeta } from "./styles";
import { marcadoresDoAmbiente, rotuloPrazoLote } from "./visoes";

// Acima disso o card vira lista longa; o resto fica no painel do lote.
const MAX_AMBIENTES_NO_CARD = 6;

function plural(n, um, varios) {
  return `${n} ${n === 1 ? um : varios}`;
}

function descreverMarcador(m) {
  if (m.naoSeAplica) return `${m.titulo}: não se aplica`;
  if (m.feito) return `${m.titulo}: ${formatarData(m.data)}`;
  return m.proxima ? `${m.titulo}: próxima` : `${m.titulo}: pendente`;
}

// Sem data limite: neutro. Os demais estados levam a cor do status do lote
// (atrasado, hoje, esta semana, no prazo), para bater o olho e separar.
function tomDoPrazo(lote) {
  return lote.dataLimiteMaquinas ? lote.statusVariant || "default" : "default";
}

function LinhaAmbiente({ ambiente }) {
  const marcadores = marcadoresDoAmbiente(ambiente);
  return (
    <Ambiente>
      <span title={ambiente.ambiente}>{ambiente.ambiente}</span>
      <Marcadores aria-label={marcadores.map(descreverMarcador).join(", ")}>
        {marcadores.map((m) => (
          <Marcador
            key={m.chave}
            $feito={m.feito}
            $proxima={m.proxima}
            title={descreverMarcador(m)}
            aria-hidden
          >
            {m.sigla}
          </Marcador>
        ))}
      </Marcadores>
    </Ambiente>
  );
}

function CardLoteKanban({ lote, onSelectLote }) {
  const pendentes = lote.ambientes.filter((a) => a.pendente);
  const visiveis = pendentes.slice(0, MAX_AMBIENTES_NO_CARD);
  const ocultos = pendentes.length - visiveis.length;
  const concluidos = lote.totalAmbientes - lote.totalPendentes;

  const rodape = [
    ocultos > 0 ? `+${plural(ocultos, "ambiente pendente", "ambientes pendentes")}` : null,
    concluidos > 0 ? `${plural(concluidos, "concluído", "concluídos")} nas máquinas` : null,
  ].filter(Boolean);

  const tom = tomDoPrazo(lote);

  return (
    <Card type="button" $tone={tom} onClick={() => onSelectLote(lote)}>
      <CardTopo $tone={tom}>
        <CardLote>{lote.loteLabel}</CardLote>
        <Prazo $tone={tom} title={`Limite das máquinas: ${formatarData(lote.dataLimiteMaquinas)}`}>
          {rotuloPrazoLote(lote.dataLimiteMaquinas)}
        </Prazo>
        <CardCliente title={lote.clientes.join(", ")}>{lote.clienteResumo}</CardCliente>
      </CardTopo>
      <Ambientes>
        {visiveis.map((ambiente, index) => (
          <LinhaAmbiente key={`${ambiente.ambiente}-${index}`} ambiente={ambiente} />
        ))}
      </Ambientes>
      {rodape.length > 0 && <CardRodape>{rodape.join(" · ")}</CardRodape>}
    </Card>
  );
}

// Por operação: quantos ambientes pendentes do lote já fizeram, e o estado
// dominante (feito em todos / é a próxima de algum / fica para depois).
export function resumoOperacoesDoLote(lote) {
  const pendentes = lote.ambientes.filter((a) => a.pendente);
  const porChave = new Map();
  pendentes.forEach((ambiente) => {
    marcadoresDoAmbiente(ambiente).forEach((m) => {
      const atual = porChave.get(m.chave) || { sigla: m.sigla, titulo: m.titulo, feitos: 0, proxima: false };
      if (m.feito) atual.feitos += 1;
      if (m.proxima) atual.proxima = true;
      porChave.set(m.chave, atual);
    });
  });
  return [...porChave.entries()].map(([chave, op]) => ({
    chave,
    ...op,
    total: pendentes.length,
    tom: op.feitos === pendentes.length ? "success" : op.proxima ? "proxima" : "depois",
  }));
}

function ativarComTeclado(event, acao) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    acao();
  }
}

/* Card compacto (≤1024px): lote / selo do prazo / cliente · ambientes /
   C F B com feitos do total pendente. Toque abre o mesmo LoteDrawer. */
function CardLoteCompacto({ lote, onSelectLote }) {
  const tom = tomDoPrazo(lote);
  const abrir = () => onSelectLote(lote);
  const ops = resumoOperacoesDoLote(lote);
  return (
    <CardItem
      role="button"
      tabIndex={0}
      $clicavel
      $barra={tom}
      data-testid="industrializacao-card-lote"
      onClick={abrir}
      onKeyDown={(event) => ativarComTeclado(event, abrir)}
    >
      <CardTopoCompacto>
        <CardTitulo>{lote.loteLabel}</CardTitulo>
        <span className="selo">
          <Prazo $tone={tom} title={`Limite das máquinas: ${formatarData(lote.dataLimiteMaquinas)}`}>
            {rotuloPrazoLote(lote.dataLimiteMaquinas)}
          </Prazo>
        </span>
      </CardTopoCompacto>
      <CardSub title={lote.clientes.join(", ")}>
        {lote.clienteResumo} · {plural(lote.totalPendentes, "ambiente", "ambientes")}
      </CardSub>
      <CardMeta aria-label={ops.map((op) => `${op.titulo}: ${op.feitos} de ${op.total}`).join(", ")}>
        {ops.map((op, i) => (
          <span className="par" key={op.chave}>
            {i > 0 && <span className="sep">· </span>}
            <span className="k">{op.sigla}</span> <OpMeta $tom={op.tom}>{op.feitos}/{op.total}</OpMeta>
          </span>
        ))}
      </CardMeta>
    </CardItem>
  );
}

function RaiaCelular({ colunas, onSelectLote }) {
  const [ativaId, setAtivaId] = useState(
    () => (colunas.find((col) => col.lotes.length > 0) || colunas[0]).chave
  );
  const ativa = colunas.find((col) => col.chave === ativaId) || colunas[0];
  return (
    <div data-testid="kanban-industrializacao-celular">
      <LaneTabs role="tablist" aria-label="Operação">
        {colunas.map((col) => (
          <LaneChip
            key={col.chave}
            type="button"
            role="tab"
            aria-selected={col.chave === ativa.chave}
            $on={col.chave === ativa.chave}
            onClick={() => setAtivaId(col.chave)}
          >
            {col.titulo} <span className="n">{col.lotes.length}</span>
            {col.atrasados > 0 && <span className="atraso">· {col.atrasados}</span>}
          </LaneChip>
        ))}
      </LaneTabs>
      {ativa.lotes.length === 0 ? (
        <ColunaVazia>Nenhum lote aguardando {ativa.titulo.toLowerCase()}</ColunaVazia>
      ) : (
        <Lista aria-label={`Aguardando ${ativa.titulo.toLowerCase()}`}>
          {ativa.lotes.map((lote) => (
            <CardLoteCompacto key={lote.id} lote={lote} onSelectLote={onSelectLote} />
          ))}
        </Lista>
      )}
    </div>
  );
}

export default function KanbanView({ lotesPorOperacao, onSelectLote }) {
  const compacto = useMediaQuery(mq.tablet);
  const celular = useMediaQuery(mq.mobile);
  const colunas = getColunasMaquinas().map((operacao) => {
    const lotes = lotesPorOperacao[operacao.chave];
    return {
      chave: operacao.chave,
      titulo: operacao.titulo,
      lotes,
      ambientes: lotes.reduce((soma, lote) => soma + lote.totalPendentes, 0),
      atrasados: lotes.filter((lote) => lote.statusVariant === "danger").length,
    };
  });

  if (colunas.every((col) => col.lotes.length === 0)) {
    return <EmptyState>Nenhum lote aguardando {LISTA_OPERACOES}.</EmptyState>;
  }

  if (celular) {
    return <RaiaCelular colunas={colunas} onSelectLote={onSelectLote} />;
  }

  const CardDoLote = compacto ? CardLoteCompacto : CardLoteKanban;

  return (
    <>
      <Legenda>
        <span>C corte · F furação · B bordo</span>
        <span>
          <Marcador $feito aria-hidden>C</Marcador> feito
        </span>
        <span>
          <Marcador $proxima aria-hidden>F</Marcador> próxima
        </span>
        <span>
          <Marcador aria-hidden>B</Marcador> depois
        </span>
      </Legenda>
      <Colunas>
        {colunas.map((col) => (
          <Coluna key={col.chave} aria-label={`Aguardando ${col.titulo.toLowerCase()}`}>
            <ColunaTopo>
              <ColunaTitulo>{col.titulo}</ColunaTitulo>
              <ColunaContagem>
                {col.atrasados > 0 && (
                  <ColunaAtraso>{plural(col.atrasados, "atrasado", "atrasados")}</ColunaAtraso>
                )}
                <span>
                  {plural(col.lotes.length, "lote", "lotes")} · {col.ambientes} amb.
                </span>
              </ColunaContagem>
            </ColunaTopo>
            {col.lotes.length === 0 ? (
              <ColunaVazia>Nenhum lote aguardando {col.titulo.toLowerCase()}</ColunaVazia>
            ) : (
              <Lista>
                {col.lotes.map((lote) => (
                  <CardDoLote key={lote.id} lote={lote} onSelectLote={onSelectLote} />
                ))}
              </Lista>
            )}
          </Coluna>
        ))}
      </Colunas>
    </>
  );
}
